import type { RealtimeMessage } from '@atlas/types'
import { EventType, FileJobStatus } from '@atlas/types'
import type { QueryClient } from '@tanstack/react-query'
import { logger } from '@/lib/logger'
import { notification } from '@/notifications/notification-store'
import {
	downloadZipResult,
	reconcileFileJobRequest,
	TERMINAL_FILE_JOB_STATUSES,
	type ZipRequestSummary,
	zipJobListKey,
} from '@/services/file-job.service'
import { getRealtimeConnectionState, RealtimeConnectionState, subscribeRealtime } from './realtime-dispatcher'

// Follows every ZIP request accepted this session until it reaches a terminal
// state, wherever the user navigates and with no page mounted — which is why it
// is a module store registered from `RealtimeProvider`, like the query bridge and
// the notification bridge beside it, rather than a hook.
//
// In memory only: a browser refresh takes the whole map with it and nothing is
// reconstructed. Work that outlives the refresh is recovered by Library's own
// backend-backed listing instead, which is a different surface with different
// rules (no global notification for what it recovers).

const log = logger.child('file-job-monitor')

/** Backoff steps while the push channel is down. Implementation defaults, not a product guarantee. */
const DISCONNECTED_BACKOFF_MS = [3_000, 6_000, 12_000, 24_000, 30_000]

/**
 * Cadence while the push channel is healthy. Reduced, never off: a realtime
 * message is best-effort, so switching the safety check off entirely (as
 * `useThumbnails` does) would make a dropped message an outcome the user is
 * never told about.
 */
const CONNECTED_INTERVAL_MS = 45_000

interface TrackedRequest {
	requestId: string
	jobId: string
	attempt: number
	timer: ReturnType<typeof setTimeout> | null
	/** True while a read for this request is in flight, so a hint arriving mid-poll is a no-op. */
	reconciling: boolean
}

const tracked = new Map<string, TrackedRequest>()
let activeQueryClient: QueryClient | null = null

/** ms until this entry's next read. Backing off while disconnected; flat and infrequent while connected. */
function nextDelay(entry: TrackedRequest): number {
	if (getRealtimeConnectionState() === RealtimeConnectionState.CONNECTED) return CONNECTED_INTERVAL_MS
	const step = Math.min(entry.attempt, DISCONNECTED_BACKOFF_MS.length - 1)
	return DISCONNECTED_BACKOFF_MS[step] as number
}

function schedule(entry: TrackedRequest, delayMs = nextDelay(entry)): void {
	if (entry.timer !== null) clearTimeout(entry.timer)
	entry.timer = setTimeout(() => {
		void tick(entry.requestId)
	}, delayMs)
}

/** Stop following one request. Called before its outcome is announced, so a message racing in finds nothing tracked. */
function untrack(requestId: string): void {
	const entry = tracked.get(requestId)
	if (!entry) return
	if (entry.timer !== null) clearTimeout(entry.timer)
	tracked.delete(requestId)
}

/** The one terminal announcement for a session-followed request. `EXPIRED` is not a completion anyone was waiting on. */
function announce(queryClient: QueryClient, summary: ZipRequestSummary): void {
	const download = {
		label: 'Download',
		onPress: () => {
			void downloadZipResult(queryClient, summary.requestId)
		},
	}
	if (summary.status === FileJobStatus.FAILED) {
		notification.error(`${summary.fileName} could not be prepared.`)
		return
	}
	if (summary.status !== FileJobStatus.READY) return
	const unavailable = summary.unavailableCount ?? 0
	if (unavailable > 0) {
		notification.warning(
			`${summary.fileName} is ready. ${unavailable} ${unavailable === 1 ? 'file' : 'files'} could not be included.`,
			{ action: download },
		)
		return
	}
	notification.success(`${summary.fileName} is ready.`, { action: download })
}

/** Terminal path: stop following, let every surface converge, then announce exactly once. */
function terminalize(queryClient: QueryClient, summary: ZipRequestSummary): void {
	untrack(summary.requestId)
	void queryClient.invalidateQueries({ queryKey: zipJobListKey() })
	announce(queryClient, summary)
}

/**
 * One authoritative read for one tracked request, and what its answer does.
 *
 * Only a terminal `found` ever ends the following. A `not-found` stops it
 * silently — the request is no longer addressable, which is neither a success
 * nor a failure to tell anyone about. An unusable body and a thrown transient
 * failure both leave the request exactly as it was and simply try again.
 */
async function tick(requestId: string): Promise<void> {
	const entry = tracked.get(requestId)
	const queryClient = activeQueryClient
	if (!entry || !queryClient || entry.reconciling) return

	entry.reconciling = true
	try {
		const read = await reconcileFileJobRequest(queryClient, requestId)
		if (!tracked.has(requestId)) return
		if (read.kind === 'not-found') {
			untrack(requestId)
			void queryClient.invalidateQueries({ queryKey: zipJobListKey() })
			return
		}
		if (read.kind === 'found' && TERMINAL_FILE_JOB_STATUSES.has(read.summary.status)) {
			terminalize(queryClient, read.summary)
			return
		}
		if (read.kind === 'unusable') log.warn('Unusable status read; the request stays tracked')
		entry.attempt += 1
		schedule(entry)
	} catch (error) {
		log.warn('Status read failed; the request stays tracked', error)
		if (!tracked.has(requestId)) return
		entry.attempt += 1
		schedule(entry)
	} finally {
		entry.reconciling = false
	}
}

/** The `requestId` a terminal message names, read defensively — the payload is a hint, never a shape to trust. */
function requestIdOf(message: RealtimeMessage): string | null {
	const data = message.data as { requestId?: unknown } | undefined
	return typeof data?.requestId === 'string' && data.requestId.length > 0 ? data.requestId : null
}

/**
 * Start following ZIP requests accepted this session. Called once by
 * `RealtimeProvider`, beside the query and notification bridges.
 *
 * The subscription is unnarrowed by `entityRef.id` on purpose: for a File Job
 * event that id is the SHARED job, which several requesters' requests can point
 * at, so the only thing that identifies this requester's request is
 * `data.requestId`.
 *
 * @param {QueryClient} queryClient - Used for every authoritative read and every invalidation.
 * @returns {() => void} Unsubscribe. Does NOT clear tracked requests — see {@link resetFileJobMonitor}.
 */
export function registerFileJobMonitor(queryClient: QueryClient): () => void {
	activeQueryClient = queryClient
	return subscribeRealtime({ type: [EventType.FILE_JOB_READY, EventType.FILE_JOB_FAILED] }, (message) => {
		const requestId = requestIdOf(message)
		if (!requestId) return
		const entry = tracked.get(requestId)
		// A message for something this session is not following — another tab's
		// request, or one already terminalized — is ignored without effect. The
		// message's own claimed status is never read: it only brings the next
		// authoritative read forward.
		if (!entry || entry.reconciling) return
		schedule(entry, 0)
	})
}

/**
 * Follow one accepted request until it is terminal.
 *
 * A plain function, not a hook: it is called from a mutation's `onSuccess`,
 * exactly the way `notification.error(...)` already is.
 *
 * @param {object} request - The accepted request, as the creation response reported it.
 */
export function trackRequest(request: { requestId: string; jobId: string; status: FileJobStatus }): void {
	if (TERMINAL_FILE_JOB_STATUSES.has(request.status)) return
	if (tracked.has(request.requestId)) return
	const entry: TrackedRequest = {
		requestId: request.requestId,
		jobId: request.jobId,
		attempt: 0,
		timer: null,
		reconciling: false,
	}
	tracked.set(request.requestId, entry)
	schedule(entry)
}

/**
 * Drop every tracked request and its timer.
 *
 * Wired into `RealtimeProvider`'s CONNECTION-lifecycle cleanup rather than the
 * registration effect's: that is the one boundary in this app that represents
 * "the authenticated session ended", which is exactly when the monitor should
 * stop asking on that session's behalf. The realtime subscription is a different
 * lifetime and is left alone here.
 */
export function resetFileJobMonitor(): void {
	for (const entry of tracked.values()) {
		if (entry.timer !== null) clearTimeout(entry.timer)
	}
	tracked.clear()
}

/** Which requests are currently followed. For tests — production code never needs to ask. */
export function getTrackedRequestIds(): string[] {
	return [...tracked.keys()]
}
